// =============================================================================
// BrightPath - Health Log Statistics React Query Hooks
// Package: BP-03-04-P1 (Health Frontend - Foundation)
// =============================================================================

import {
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { healthApi } from '@/features/health/api/healthApi';
import type {
  HealthLogStats,
  SeizureStats,
  SleepStats,
  MoodStats,
  MedicationAdherenceStats,
  StatsParams,
} from '@/features/health/types/healthTypes';

// =============================================================================
// Query Keys
// =============================================================================

export const healthStatsKeys = {
  all: ['healthLogStats'] as const,
  overview: (childId: string, params?: StatsParams) =>
    [...healthStatsKeys.all, 'overview', childId, params] as const,
  seizures: (childId: string, params?: StatsParams) =>
    [...healthStatsKeys.all, 'seizures', childId, params] as const,
  sleep: (childId: string, params?: StatsParams) =>
    [...healthStatsKeys.all, 'sleep', childId, params] as const,
  mood: (childId: string, params?: StatsParams) =>
    [...healthStatsKeys.all, 'mood', childId, params] as const,
  medicationAdherence: (childId: string, params?: StatsParams) =>
    [...healthStatsKeys.all, 'medicationAdherence', childId, params] as const,
};

// =============================================================================
// Default Stats Parameters
// =============================================================================

const defaultStatsParams: StatsParams = {
  period: 'month',
  compareWithPrevious: true,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetch overall health log statistics
 */
export function useHealthLogStats(
  childId: string,
  params?: StatsParams,
  options?: Omit<UseQueryOptions<HealthLogStats, Error>, 'queryKey' | 'queryFn'>
) {
  const mergedParams = { ...defaultStatsParams, ...params };
  
  return useQuery({
    queryKey: healthStatsKeys.overview(childId, mergedParams),
    queryFn: () => healthApi.getHealthLogStats(childId, mergedParams),
    enabled: !!childId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
}

/**
 * Fetch seizure-specific statistics
 */
export function useSeizureStats(
  childId: string,
  params?: StatsParams,
  options?: Omit<UseQueryOptions<SeizureStats, Error>, 'queryKey' | 'queryFn'>
) {
  const mergedParams = { ...defaultStatsParams, ...params };
  
  return useQuery({
    queryKey: healthStatsKeys.seizures(childId, mergedParams),
    queryFn: () => healthApi.getSeizureStats(childId, mergedParams),
    enabled: !!childId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    ...options,
  });
}

/**
 * Fetch sleep-specific statistics
 */
export function useSleepStats(
  childId: string,
  params?: StatsParams,
  options?: Omit<UseQueryOptions<SleepStats, Error>, 'queryKey' | 'queryFn'>
) {
  const mergedParams = { ...defaultStatsParams, ...params };
  
  return useQuery({
    queryKey: healthStatsKeys.sleep(childId, mergedParams),
    queryFn: () => healthApi.getSleepStats(childId, mergedParams),
    enabled: !!childId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    ...options,
  });
}

/**
 * Fetch mood-specific statistics
 */
export function useMoodStats(
  childId: string,
  params?: StatsParams,
  options?: Omit<UseQueryOptions<MoodStats, Error>, 'queryKey' | 'queryFn'>
) {
  const mergedParams = { ...defaultStatsParams, ...params };
  
  return useQuery({
    queryKey: healthStatsKeys.mood(childId, mergedParams),
    queryFn: () => healthApi.getMoodStats(childId, mergedParams),
    enabled: !!childId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    ...options,
  });
}

/**
 * Fetch medication adherence statistics
 */
export function useMedicationAdherenceStats(
  childId: string,
  params?: StatsParams,
  options?: Omit<
    UseQueryOptions<MedicationAdherenceStats, Error>,
    'queryKey' | 'queryFn'
  >
) {
  const mergedParams = { ...defaultStatsParams, ...params };
  
  return useQuery({
    queryKey: healthStatsKeys.medicationAdherence(childId, mergedParams),
    queryFn: () => healthApi.getMedicationAdherenceStats(childId, mergedParams),
    enabled: !!childId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    ...options,
  });
}

// =============================================================================
// Combined Stats Hook (for dashboard)
// =============================================================================

interface DashboardStats {
  overview: HealthLogStats | undefined;
  seizures: SeizureStats | undefined;
  sleep: SleepStats | undefined;
  mood: MoodStats | undefined;
  medicationAdherence: MedicationAdherenceStats | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Fetch all stats needed for the health dashboard
 */
export function useHealthDashboardStats(
  childId: string,
  params?: StatsParams
): DashboardStats {
  const overviewQuery = useHealthLogStats(childId, params);
  const seizuresQuery = useSeizureStats(childId, params);
  const sleepQuery = useSleepStats(childId, params);
  const moodQuery = useMoodStats(childId, params);
  const adherenceQuery = useMedicationAdherenceStats(childId, params);

  const isLoading =
    overviewQuery.isLoading ||
    seizuresQuery.isLoading ||
    sleepQuery.isLoading ||
    moodQuery.isLoading ||
    adherenceQuery.isLoading;

  const isError =
    overviewQuery.isError ||
    seizuresQuery.isError ||
    sleepQuery.isError ||
    moodQuery.isError ||
    adherenceQuery.isError;

  const error =
    overviewQuery.error ||
    seizuresQuery.error ||
    sleepQuery.error ||
    moodQuery.error ||
    adherenceQuery.error;

  return {
    overview: overviewQuery.data,
    seizures: seizuresQuery.data,
    sleep: sleepQuery.data,
    mood: moodQuery.data,
    medicationAdherence: adherenceQuery.data,
    isLoading,
    isError,
    error,
  };
}

// =============================================================================
// Trend Analysis Helpers
// =============================================================================

export type TrendDirection = 'up' | 'down' | 'stable';

interface TrendInfo {
  direction: TrendDirection;
  percentChange: number;
  isSignificant: boolean;
}

/**
 * Analyze trend from percent change value
 */
export function analyzeTrend(
  percentChange: number,
  significanceThreshold: number = 5
): TrendInfo {
  const absChange = Math.abs(percentChange);
  
  let direction: TrendDirection = 'stable';
  if (percentChange > significanceThreshold) {
    direction = 'up';
  } else if (percentChange < -significanceThreshold) {
    direction = 'down';
  }

  return {
    direction,
    percentChange,
    isSignificant: absChange >= significanceThreshold,
  };
}

/**
 * Get trend info for seizure frequency (lower is better)
 */
export function useSeizureTrend(childId: string, params?: StatsParams) {
  const { data: stats } = useSeizureStats(childId, params);
  
  if (!stats) return null;
  
  const trend = analyzeTrend(stats.averagePerWeek);
  
  // For seizures, down is good, up is bad
  return {
    ...trend,
    isPositive: trend.direction === 'down',
    label:
      trend.direction === 'down'
        ? 'Decreasing'
        : trend.direction === 'up'
        ? 'Increasing'
        : 'Stable',
  };
}

/**
 * Get trend info for sleep quality (higher is better)
 */
export function useSleepTrend(childId: string, params?: StatsParams) {
  const { data: stats } = useSleepStats(childId, params);
  
  if (!stats) return null;
  
  // Calculate trend based on average quality change
  // This would need period comparison data from backend
  return {
    averageQuality: stats.averageQuality,
    averageDuration: stats.averageDurationHours,
  };
}

/**
 * Get seizure-free days streak
 */
export function useSeizureFreeStreak(childId: string) {
  const { data: stats, isLoading } = useSeizureStats(childId);
  
  return {
    currentStreak: stats?.currentSeizureFreeStreak ?? 0,
    longestStreak: stats?.longestSeizureFreeStreak ?? 0,
    isLoading,
  };
}

// =============================================================================
// Summary Calculations
// =============================================================================

interface HealthSummary {
  seizureFreeStreak: number;
  averageSleepHours: number;
  averageMoodScore: number;
  medicationAdherenceRate: number;
  therapySessionsThisMonth: number;
}

/**
 * Calculate health summary from all stats
 */
export function calculateHealthSummary(stats: DashboardStats): HealthSummary {
  return {
    seizureFreeStreak: stats.seizures?.currentSeizureFreeStreak ?? 0,
    averageSleepHours: stats.sleep?.averageDurationHours ?? 0,
    averageMoodScore: stats.mood?.averageMoodScore ?? 0,
    medicationAdherenceRate: stats.medicationAdherence?.adherenceRate ?? 0,
    therapySessionsThisMonth:
      stats.overview?.logsByType?.['therapy'] ?? 0,
  };
}

// =============================================================================
// Prefetch Helper
// =============================================================================

import { useQueryClient } from '@tanstack/react-query';

/**
 * Prefetch all stats for a child (useful for navigation)
 */
export function usePrefetchHealthStats(childId: string) {
  const queryClient = useQueryClient();
  const params = defaultStatsParams;

  return () => {
    // Prefetch all stat queries in parallel
    Promise.all([
      queryClient.prefetchQuery({
        queryKey: healthStatsKeys.overview(childId, params),
        queryFn: () => healthApi.getHealthLogStats(childId, params),
        staleTime: 1000 * 60 * 5,
      }),
      queryClient.prefetchQuery({
        queryKey: healthStatsKeys.seizures(childId, params),
        queryFn: () => healthApi.getSeizureStats(childId, params),
        staleTime: 1000 * 60 * 5,
      }),
      queryClient.prefetchQuery({
        queryKey: healthStatsKeys.sleep(childId, params),
        queryFn: () => healthApi.getSleepStats(childId, params),
        staleTime: 1000 * 60 * 5,
      }),
    ]);
  };
}

